import React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { BillDocumentSection } from "@/components/bill-document-section/bill-document-section"
import { BillDetailReadonlyFields } from "@/components/bills/bill-detail-readonly-fields"
import { BillChargesSection } from "@/components/bills/bill-line-items-display"
import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { useSiteLayoutContext } from "@/modules/site/context/site-layout-context"
import { useBillPeriod } from "@/services/bill-period/use-bill-period"
import { isUnavailableBillError } from "@/services/bill/use-bill"
import type { BillPeriod } from "@/services/core-api/types"
import { useMeters } from "@/services/meter"
import { parseModelId } from "@/utils/parse-model-id"
import { getDisplayValue } from "@/utils/value"

import { Box, Skeleton, Stack, Typography } from "@mui/material"

// The page shows the period's as-ingested usage and its cost.
const toBillSummary = (period: BillPeriod) => ({
  cost: period.cost_major ?? null,
  currency: period.currency,
  end_date: period.end_date,
  flow_direction: period.flow_direction,
  provider_name: period.provider_name,
  resource_type: period.resource_type,
  start_date: period.start_date,
  usage: period.raw_usage,
  usage_unit: period.raw_usage_unit,
})

export const SiteBillRoute: React.FC = () => {
  const { orgId, site } = useSiteLayoutContext()
  const { billId: billIdParam } = useParams()
  const { t } = useTranslation("sites")

  const billPeriodId = parseModelId(billIdParam)
  const siteId = site?.id

  const {
    billPeriodData,
    billPeriodError,
    billPeriodIsFetched,
    billPeriodIsLoading,
  } = useBillPeriod({ billPeriodId, organizationId: orgId })

  const meterId = billPeriodData?.meter_id
  const {
    metersData,
    metersIsFetched,
    metersIsLoading,
    metersIsPlaceholderData,
  } = useMeters({
    apiQueryOptions: {
      pageSize: 1,
      where: {
        id: meterId !== undefined ? [meterId] : [],
        organization_id: orgId,
      },
    },
    queryOptions: {
      enabled: Boolean(orgId) && meterId !== undefined,
    },
  })

  const billMeter = metersIsPlaceholderData ? undefined : metersData?.[0]
  const billSiteId =
    billMeter?.site?.id ??
    (billMeter?.siteId != null ? String(billMeter.siteId) : undefined)

  if (!orgId || !siteId) {
    return null
  }

  const isExpectedBillUnavailableError = isUnavailableBillError(billPeriodError)

  // An interval-derived period has no bill behind it, so it is unavailable
  // here even when the gateway returns it.
  const isBillUnavailable =
    billPeriodId === undefined ||
    (billPeriodIsFetched &&
      (billPeriodData == null ||
        isExpectedBillUnavailableError ||
        billPeriodData.is_interval_derived))

  const isMeterLookupPending =
    billPeriodData != null &&
    (metersIsLoading || metersIsPlaceholderData || !metersIsFetched)

  const isSiteMismatch =
    billPeriodIsFetched &&
    !isMeterLookupPending &&
    billSiteId != null &&
    billSiteId !== siteId

  const isLoading =
    billPeriodIsLoading ||
    (!billPeriodIsFetched && billPeriodId !== undefined) ||
    isMeterLookupPending

  if (isBillUnavailable || isSiteMismatch) {
    return (
      <Stack sx={{ gap: 2 }}>
        <Typography component="h3" variant="h3">
          {t("bills.detail.title")}
        </Typography>
        <Box sx={{ minHeight: "50vh", position: "relative" }}>
          <Page404 />
        </Box>
      </Stack>
    )
  }

  const billFieldRows =
    isLoading || !billPeriodData ? (
      <SiteBillDetailSkeleton />
    ) : (
      <BillDetailReadonlyFields
        bill={toBillSummary(billPeriodData)}
        meterValue={getDisplayValue(billMeter?.meterCode)}
      />
    )

  return (
    <Stack sx={{ gap: 2 }}>
      <Typography component="h3" variant="h3">
        {t("bills.detail.title")}
      </Typography>
      <PageCard>
        <Stack sx={{ gap: 4 }}>
          <Stack sx={{ gap: 2 }}>{billFieldRows}</Stack>
          {!isLoading && billPeriodData ? (
            <BillChargesSection
              isError={false}
              isSettled
              lines={billPeriodData.line_items ?? undefined}
            />
          ) : null}
          <BillDocumentSection
            bill={billPeriodData ?? null}
            isLoading={isLoading || !billPeriodData}
            pdfOnly
          />
        </Stack>
      </PageCard>
    </Stack>
  )
}

const SiteBillDetailSkeleton: React.FC = () => (
  <>
    {Array.from({ length: 6 }, (_, index) => (
      <Box key={index}>
        <Skeleton height={20} sx={{ mb: 0.5 }} width={120} />
        <Skeleton height={40} width="100%" />
      </Box>
    ))}
  </>
)
